import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { Emitter, LinkedListContainer } from "@pixi/particle-emitter";
import { GlowFilter } from "pixi-filters";
import { DEG_TO_RAD, Point } from "pixi.js";
import { Firework } from "../../../utils/Firework";
import { GameMathUtil } from "../../../utils/GameMathUtil";
import { GameSymbolUtil } from "../../../utils/GameSymbolUtil";
import { GameSoundEvent } from "../../../controller/event/GameSoundEvent";
/**
 * A firework burst animation that launches fireworks in random directions.
 */
export class GameFireworkBurst extends AbstractGameView {
    create() {
        super.create();
        this.fireworks = this.uiProperties.colours.map((colour, i) => {
            const fireworkTailContainer = new LinkedListContainer();
            const fireworkTailGlowFilter = new GlowFilter({
                ...this.uiProperties.glowProperties,
                color: colour,
            });
            fireworkTailContainer.filters = [fireworkTailGlowFilter];
            const fireworkTailEmitter = new Emitter(fireworkTailContainer, this.uiProperties.tailProperties);
            this.addChild(fireworkTailContainer);
            const fireworkHeadSpine = TitanSpine.createSpine(this, `firework-${i}`, this.landscapeProperties.headProperties, this.portraitProperties?.headProperties);
            GameSymbolUtil.setVariantSkin(fireworkHeadSpine, i % GameSymbolUtil.SYMBOL_VARIANTS);
            SpineUtil.stop(fireworkHeadSpine);
            const firework = new Firework(fireworkHeadSpine, fireworkTailEmitter);
            return firework;
        });
        this.timer = new Timer(1 / this.uiProperties.frequency);
        this.timer.onUpdate(this.launchFirework, this);
    }
    postCreate() {
        super.postCreate();
        this.nextDirection = 1;
        this.fireworksIdle = this.fireworks.slice();
    }
    start() {
        if (!this.timer.isRunning()) {
            this.dispatchGame(GameSoundEvent.FIREWORK_LOOP);
            this.timer.start();
            this.launchFirework();
        }
    }
    stop() {
        if (this.timer.isRunning()) {
            this.dispatchGame(GameSoundEvent.FIREWORK_LOOP_END);
            this.timer.stop();
        }
    }
    async launchFirework() {
        if (this.fireworksIdle.length === 0) {
            return;
        }
        const index = RandomUtil.nextInt(0, this.fireworksIdle.length);
        const firework = this.fireworksIdle[index];
        // Generate a random speed, distance, and duration from the same factor.
        const speed = RandomUtil.nextDouble();
        const distance = GameMathUtil.lerpMinMax(this.uiProperties.distance, speed);
        const duration = GameMathUtil.lerpMinMax(this.uiProperties.duration, speed);
        // Generate a random offset and angle from the centre.
        const offset = GameMathUtil.lerpMinMax(this.uiProperties.offset, RandomUtil.nextDouble());
        const angle = GameMathUtil.lerpMinMax(this.uiProperties.angle, RandomUtil.nextDouble()) * DEG_TO_RAD;
        // Flip the horizontal based on the next direction.
        const horizontal = Math.cos(angle) * this.nextDirection;
        this.nextDirection = -this.nextDirection;
        // Exaggerate the vertical based on the aspect ratio of the viewport.
        const aspectRatio = this.renderer.getViewport().width / this.renderer.getViewport().height;
        const vertical = Math.sin(angle) / Math.min(Math.max(aspectRatio, 0.5), 1.5);
        // Determine the launch and target positions.
        const launchPosition = new Point(horizontal * offset, vertical * offset);
        const targetPosition = new Point(launchPosition.x + horizontal * distance, launchPosition.y + vertical * distance);
        // Launch the firework, temporarily removing it from the idle list until it has exploded.
        this.fireworksIdle.splice(index, 1);
        await firework.launch(launchPosition, targetPosition, duration);
        await firework.detonate();
        this.fireworksIdle.push(firework);
    }
}
//# sourceMappingURL=GameFireworkBurst.js.map